import http from '@/api/http'
import { protectedRequest } from '@/api/protectedRequest'
import { API_BASE_URL } from '@/config/env'
import { mapOrder, mapOrderList } from '@/entities/order/model/mapOrder'

export const RESTAURANT_ORDERS_LIST_TAB = {
  ACTIVE: 'active',
  ARCHIVE: 'archive',
}

export const RESTAURANT_ORDERS_MOBILE_PAGE_SIZE = 4
export const RESTAURANT_ORDERS_DESKTOP_PAGE_SIZE = 5

const ARCHIVE_BACKEND_STATUSES = ['COMPLETED', 'CANCELLED']

export function sortRestaurantOrdersNewestFirst(orders) {
  return [...orders].sort(
    (a, b) => new Date(b.createdAt ?? 0) - new Date(a.createdAt ?? 0),
  )
}

function hasOrdersApi() {
  return Boolean(API_BASE_URL)
}

function unwrapOrderListPayload(body) {
  const payload = body?.data ?? body
  const total = Number(body?.total ?? payload?.total) || 0
  if (Array.isArray(payload)) {
    return {
      items: payload,
      total: total || payload.length,
    }
  }
  if (Array.isArray(payload?.items)) {
    return {
      items: payload.items,
      total: total || payload.items.length,
    }
  }
  return {
    items: [],
    total: 0,
  }
}

function mapOrderFromBody(body) {
  return mapOrder(body?.data ?? body?.order ?? body)
}

function buildStatusFilter(listTab) {
  if (listTab === RESTAURANT_ORDERS_LIST_TAB.ARCHIVE) {
    return { $in: ARCHIVE_BACKEND_STATUSES }
  }
  return { $nin: ARCHIVE_BACKEND_STATUSES }
}

export async function fetchRestaurantOrdersApi({
  skip = 0,
  limit = RESTAURANT_ORDERS_MOBILE_PAGE_SIZE,
  listTab = RESTAURANT_ORDERS_LIST_TAB.ACTIVE,
} = {}) {
  if (!hasOrdersApi()) {
    return { items: [], total: 0 }
  }

  const queryBody = {
    skip: Math.max(0, Number(skip) || 0),
    limit: Math.max(1, Number(limit) || RESTAURANT_ORDERS_MOBILE_PAGE_SIZE),
    filter: {
      status: buildStatusFilter(listTab),
    },
    sort: { created_at: -1 },
  }

  const body = await protectedRequest(() => http.post('/restaurant/orders', queryBody))
  const mapped = mapOrderList(unwrapOrderListPayload(body))
  return {
    items: sortRestaurantOrdersNewestFirst(mapped.items),
    total: Number(mapped.total) || 0,
  }
}

export async function fetchRestaurantOrderByIdApi(orderId) {
  if (!orderId || !hasOrdersApi()) {
    return null
  }

  const body = await protectedRequest(() => http.get(`/restaurant/order/${orderId}`))
  return mapOrderFromBody(body)
}

export async function markRestaurantOrderReadyApi(orderId) {
  if (!orderId || !hasOrdersApi()) {
    return null
  }

  const body = await protectedRequest(() =>
    http.post(`/restaurant/order/${orderId}/ready`, {}),
  )
  return mapOrderFromBody(body)
}

export async function rejectRestaurantOrderApi(orderId) {
  if (!orderId || !hasOrdersApi()) {
    return null
  }

  const body = await protectedRequest(() =>
    http.post(`/restaurant/order/${orderId}/reject`, {}),
  )
  return mapOrderFromBody(body)
}

export async function acceptRestaurantOrderApi(orderId, { pickupReadyAt } = {}) {
  if (!orderId || !hasOrdersApi()) {
    return null
  }

  const body = {}
  if (pickupReadyAt) {
    body.pickup_ready_at = pickupReadyAt
  }

  const acceptBody = await protectedRequest(() =>
    http.post(`/restaurant/order/${orderId}/accept`, body),
  )
  return mapOrderFromBody(acceptBody)
}
